import { gql } from '@urql/core';
import React, { useCallback, useContext, useMemo } from 'react';
import { Layout } from 'components/common/layout/Layout';
import {
    Espin_Event,
    Espin_Game_Definition_Enum,
    EventCardFragment,
    Events_IndexDocument,
    Events_IndexQuery,
} from 'generated/spin-graphql';

import { UrqlSSGForPage } from 'server/utils/serverUtils/ssg.utils';
import { ToggleButton } from '../../components/input/ToggleButton';

import { PageHeader } from '../../components/sections/PageHeader';
import { EventsHeader } from '../../components/events/EventsHeader';
import { PageListHeader } from '../../components/sections/PageListHeader';
import { EventCard } from '../../components/events/card/EventCard';
import { SearchBarContextType, SearchBarContext, createSearchBarProvider, SearchActionType } from '../../client/hooks';
import { WritableDraft } from 'immer/dist/internal';
import { useImmer } from 'use-immer';
import { useQuery } from 'urql';
import { GenericFullPageLoading } from '../../components/common/GenericFullPageLoading';
import { CustomSEO } from '../../components/common/layout/HeadMeta';
import { filterDuplicates } from '../../shared/shared.utils';

gql`
    query Events_Index {
        stickied_events: espin_event(
            where: { event_end_date: { _gte: "now()" }, status: { _eq: published }, stickied: { _is_null: false } }
            order_by: { stickied: asc }
        ) {
            id
            ...EventCard
        }
        espin_event(
            order_by: { event_start_date: asc }
            where: { event_end_date: { _gte: "now()" }, status: { _eq: published } }
        ) {
            id
            ...EventCard
        }
        espin_game_definition {
            display_name
            game_name
        }
    }
`;
const EVENTS_PER_PAGE = 30;
export function useEventsSearchContext(): SearchBarContextType<EventCardFragment> {
    return useContext(SearchBarContext);
}

export function ShowMoreEventsSearchResultsButton() {
    const { dispatch, hasMore } = useEventsSearchContext();

    if (!hasMore) {
        return <></>;
    }
    return (
        <button className="show-more button blue-button medium-button" onClick={() => dispatch({ type: 'loadMore' })}>
            SHOW MORE
        </button>
    );
}

export default function Events_Index() {
    const [result] = useQuery<Events_IndexQuery>({
        query: Events_IndexDocument,
        context: useMemo(() => ({ additionalTypenames: ['espin_event'] }), []),
    });

    const { espin_event: all_events, espin_game_definition, stickied_events } = result.data ?? {};
    const espin_event: EventCardFragment[] = useMemo(
        () => filterDuplicates([...(stickied_events ?? []), ...(all_events ?? [])], 'id'),
        [all_events, stickied_events],
    );
    const SearchBarProvider = useMemo(() => createSearchBarProvider(SearchBarContext), []);
    const pageDescription = `
    Stay Plugged In offers a wide selection of recruiting events, providing the opportunity 
    for players to compete in front of hundreds of collegiate recruiters looking for their 
    next recruit. We currently host events for the six most popular esports titles offered 
    at the collegiate level including Valorant, League of Legends, Rocket League, Overwatch 
    and Rainbow Six Siege. Stay Plugged In events fall into three main categories:`;

    const [selectedFilters, setSelectedFilters] = useImmer<Espin_Game_Definition_Enum[]>([]);

    const filterElements = useMemo(() => {
        const elements: JSX.Element[] = [];

        for (const gameDefinition of espin_game_definition ?? []) {
            elements.push(
                <ToggleButton
                    key={`toggle-button-${gameDefinition.game_name}`}
                    label={gameDefinition.display_name}
                    data={gameDefinition.game_name}
                    isToggled={selectedFilters.includes(gameDefinition.game_name as Espin_Game_Definition_Enum)}
                    onClick={(data: Espin_Game_Definition_Enum) => {
                        // modify our draft
                        setSelectedFilters((draft) => {
                            if (!draft.includes(data)) {
                                draft.push(data);
                            } else {
                                draft.splice(draft.indexOf(data), 1);
                            }
                        });
                    }}
                />,
            );
        }

        return elements;
    }, [espin_game_definition, selectedFilters, setSelectedFilters]);

    const eventSearchReducer = useCallback(
        function (
            draft: WritableDraft<{
                offset: number;
                searchData: EventCardFragment[];
                fullData: EventCardFragment[];
                hasMore: boolean;
                lastSearchText: string;
                lastSearchFiltersActive: boolean;
            }>,
            action: SearchActionType,
        ) {
            const hasGames = (gameDefinition: Espin_Game_Definition_Enum) => selectedFilters.includes(gameDefinition);
            switch (action.type) {
                case 'searchData':
                    // searchHistory.setEventSearchHistory((draft) => {
                    //     draft.eventSearch = action.value;
                    //     draft.eventFilters = { gameFilters: selectedFilters };
                    // });

                    if (action.value === '') {
                        if (action.useFilter && selectedFilters.length === 0) {
                            return {
                                offset: 0,
                                searchData: espin_event.slice(0, EVENTS_PER_PAGE),
                                fullData: espin_event,
                                hasSearched: false,
                                hasMore: espin_event.length > EVENTS_PER_PAGE,
                                lastSearchText: '',
                                lastSearchFiltersActive: false,
                            };
                        } else if (action.useFilter && selectedFilters.length > 0) {
                            // filter events by games
                            const res = espin_event.filter(
                                (e) => e.event_games.filter((eg) => hasGames(eg.game.game_definition)).length > 0,
                            );
                            return {
                                offset: 0,
                                searchData: res.slice(0, EVENTS_PER_PAGE),
                                fullData: res,
                                hasSearched: false,
                                hasMore: res.length > EVENTS_PER_PAGE,

                                lastSearchText: '',
                                lastSearchFiltersActive: false,
                            };
                        } else {
                            return {
                                offset: 0,
                                searchData: espin_event.slice(0, EVENTS_PER_PAGE),
                                fullData: espin_event,
                                hasSearched: false,
                                hasMore: espin_event.length > EVENTS_PER_PAGE,
                                lastSearchText: '',
                                lastSearchFiltersActive: false,
                            };
                        }
                    } else {
                        const re = new RegExp(`${action.value}`, 'i');
                        let found: EventCardFragment[];
                        if (action.useFilter && selectedFilters.length === 0) {
                            found = espin_event.filter((e) => re.test(e.name));
                        } else {
                            if (action.useFilter && selectedFilters.length > 0) {
                                found = espin_event
                                    .filter((e) => re.test(e.name))
                                    .filter(
                                        (e) =>
                                            e.event_games.filter((eg) => hasGames(eg.game.game_definition)).length > 0,
                                    );
                            } else {
                                // filter is empty, just a name search
                                found = espin_event.filter((e) => re.test(e.name));
                            }
                        }
                        return {
                            offset: 0,
                            searchData: found.slice(0, EVENTS_PER_PAGE),
                            fullData: found,
                            hasSearched: false,
                            hasMore: found.length > EVENTS_PER_PAGE,
                            lastSearchText: '',
                            lastSearchFiltersActive: false,
                        };
                    }
                case 'loadMore':
                    const newEndpoint = draft.offset + EVENTS_PER_PAGE;
                    return {
                        offset: draft.offset + EVENTS_PER_PAGE,
                        // we're loading more
                        hasSearched: false,
                        searchData: draft.fullData.slice(0, newEndpoint + EVENTS_PER_PAGE),
                        fullData: draft.fullData,
                        hasMore: draft.fullData.length > newEndpoint + EVENTS_PER_PAGE,
                        lastSearchText: '',
                        lastSearchFiltersActive: false,
                    };

                default:
                    break;
            }
        },
        [selectedFilters, espin_event],
    );

    const seoOverride: CustomSEO = {
        title: 'Events',
        description: pageDescription,
    };

    if (result.fetching) {
        // skeleton state...
        return <GenericFullPageLoading />;
    }

    return (
        <Layout seoOverride={seoOverride}>
            <PageHeader title={'Events'} description={pageDescription} className="events-page-header">
                <EventsHeader />
            </PageHeader>
            <SearchBarProvider
                initialData={{
                    offset: 0,
                    hasSearched: false, // only used on pages with an initial server search
                    hasMore: espin_event.length > EVENTS_PER_PAGE,
                    searchData: espin_event.slice(0, EVENTS_PER_PAGE) ?? [],
                    fullData: espin_event ?? [],
                    lastSearchText: '',
                    lastSearchFiltersActive: false,
                }}
                reducer={eventSearchReducer}
            >
                <PageListHeader
                    isStandaloneHeader={false}
                    title={'Upcoming Leagues & Events'}
                    filters={filterElements}
                    hasSearchButton={true}
                    defaultValue={''}
                    initialSearch={false}
                />
                <EventSearchResultList />
            </SearchBarProvider>
        </Layout>
    );
}

function EventSearchResultList() {
    const { data: events } = useEventsSearchContext();
    return (
        <div className="container events-list-cards">
            {events.map((event: Espin_Event, index: number) => (
                <EventCard key={`upcoming-event-card-${index}`} event={event} />
            ))}

            <ShowMoreEventsSearchResultsButton />
        </div>
    );
}

export async function getStaticProps(ctx) {
    return UrqlSSGForPage(Events_IndexDocument);
}
